import { PageHero } from "@/components/PageHero";
import { Section } from "@/components/Section";
import { useSeo } from "@/lib/seo";
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@repo/ui/core";
import { useForm } from "@repo/ui/form";
import {
  IconAlertCircle,
  IconBrandGitlab,
  IconCalendarBolt,
  IconCheck,
  IconMapPin,
} from "@repo/ui/icons";
import { useState } from "react";
import styles from "@styles/site.module.css";

const infoCards = [
  {
    icon: IconCalendarBolt,
    title: "Book a Demo",
    subtitle: "See the platform in action",
    body: "Schedule a personalized walkthrough with our team. We'll show you how Null Autos can fit into your existing workflow and infrastructure.",
  },
  {
    icon: IconBrandGitlab,
    title: "GitLab Integration",
    subtitle: "Works with your existing setup",
    body: "Already using GitLab? We integrate directly with your existing CI pipelines, workspaces, and registries. Setup takes minutes.",
  },
  {
    icon: IconMapPin,
    title: "Global Infrastructure",
    subtitle: "US & EU regions",
    body: "Infrastructure deployed close to your teams. US and EU regions available today, with APAC on the roadmap.",
  },
];

export default function Contact() {
  useSeo("/contact");

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      company: "",
      message: "",
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? "Name is required" : null),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      message: (v) => (v.trim().length === 0 ? "Message is required" : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setSending(true);
    setError(null);
    try {
      const company = values.company.trim();
      const header = `[Contact Form] ${values.name} (${values.email})${company ? ` - ${company}` : ""}`;
      const res = await fetch("https://ctrl.null.autos/api/v1/feedback/mb", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          FEEDBACK_INPUT: `${header}\n\n${values.message}`,
        }),
      });
      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }
      setSuccess(true);
    } catch {
      setError(
        "Your message could not be sent. Please try again, or email us directly at contact@null.autos."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={
          <>
            Let&apos;s build the future <span className={styles.gradientText}>together</span>
          </>
        }
        description="Schedule a demo, ask a question, or tell us about your automotive development challenges. We'd love to hear from you."
      />

      <Section>
        <Grid gutter={48}>
          <Grid.Col span={{ base: 12, md: 7 }}>
            {success ? (
              <Card padding="xl" className={styles.card}>
                <Stack align="center" gap="md" py="xl">
                  <span className={styles.iconChip} style={{ width: 56, height: 56 }}>
                    <IconCheck size={28} />
                  </span>
                  <Title order={2} ta="center" fw={650}>
                    Thank you
                  </Title>
                  <Text size="lg" c="dimmed" ta="center">
                    We&apos;ll get back to you within one business day.
                  </Text>
                </Stack>
              </Card>
            ) : (
              <Card padding="xl" className={styles.card}>
                <form onSubmit={form.onSubmit(handleSubmit)}>
                  <Stack gap="lg">
                    <Title order={3} size="1.25rem" fw={600}>
                      Send us a message
                    </Title>
                    {error && (
                      <Alert
                        variant="light"
                        color="red"
                        icon={<IconAlertCircle size={18} />}
                        role="alert"
                      >
                        {error}
                      </Alert>
                    )}
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="Name"
                          placeholder="Your name"
                          withAsterisk
                          {...form.getInputProps("name")}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="Email"
                          placeholder="you@company.com"
                          withAsterisk
                          {...form.getInputProps("email")}
                        />
                      </Grid.Col>
                    </Grid>
                    <TextInput
                      label="Company"
                      placeholder="Your company (optional)"
                      {...form.getInputProps("company")}
                    />
                    <Textarea
                      label="Message"
                      placeholder="Tell us about your project, team, or questions..."
                      withAsterisk
                      rows={6}
                      {...form.getInputProps("message")}
                    />
                    <Box>
                      <Button
                        type="submit"
                        size="md"
                        loading={sending}
                        disabled={sending}
                        style={{
                          background: "var(--na-text)",
                          color: "var(--na-bg)",
                          borderRadius: "var(--na-r-sm)",
                          fontWeight: 550,
                        }}
                      >
                        Send Message
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Card>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="lg">
              {infoCards.map((card) => (
                <Card key={card.title} padding="xl" className={styles.card}>
                  <Stack gap="md">
                    <Group gap="md" wrap="nowrap">
                      <span className={styles.iconChip}>
                        <card.icon size={20} stroke={1.5} />
                      </span>
                      <Box>
                        <Text fw={600}>{card.title}</Text>
                        <Text size="sm" c="dimmed">
                          {card.subtitle}
                        </Text>
                      </Box>
                    </Group>
                    <Text size="sm" c="dimmed" lh={1.65}>
                      {card.body}
                    </Text>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Grid.Col>
        </Grid>
      </Section>
    </>
  );
}
